import { useIntl } from 'react-intl';
import { ServiceStatisticsGranularity } from '../../dto/ServiceStatisticsDTO';
import InfoTip from '../../components/novarium/InfoTip';
import { PeakAnalysis } from './peaks';
import { chipTitle } from './TimelineChart';
import { formatInt, formatMinutes, Measure, TimelinePoint } from './statisticsUtils';

interface PeakChipsProps {
    points: TimelinePoint[];
    granularity: ServiceStatisticsGranularity;
    /** Solo «Espera» y «Duración» tienen picos. */
    measure: Exclude<Measure, 'attentions'>;
    analysis: PeakAnalysis;
    /** Abre el detalle del período; `opener` es el chip, a donde vuelve el foco al cerrar el panel. */
    onOpen: (point: TimelinePoint, opener: HTMLElement) => void;
}

/**
 * «Picos»: un botón por cada pico marcado en el gráfico («Domingo 20/09 · 162 min · 48 atenciones ›»), la entrada más
 * fácil con el dedo y con lector de pantalla. Abre el mismo detalle que el click en el gráfico. Sin picos no se muestra
 * nada. El ⓘ dice el criterio, con la cantidad mínima de atenciones que usó el cálculo.
 */
export default function PeakChips({ points, granularity, measure, analysis, onOpen }: PeakChipsProps) {
    const intl = useIntl();
    if (analysis.peaks.length === 0) return null;

    return (
        <div className="ss-peaks">
            <span className="ss-peaks__label">
                <span className="nv-strong" id="ss-peaks-label">{intl.formatMessage({ id: 'ServicesStatisticsPeaksTitle' })}</span>
                <InfoTip text={intl.formatMessage({ id: 'ServicesStatisticsPeaksCriteria' },
                    { unit: granularity, measure, min: formatInt(analysis.minAttentions) })} />
            </span>
            <ul className="ss-peaks__list" aria-labelledby="ss-peaks-label">
                {analysis.peaks.map(index => {
                    const point = points[index];
                    const value = measure === 'wait' ? point.avgWait : point.avgService;
                    return (
                        <li key={point.period}>
                            <button type="button" className="ss-peak-chip" onClick={event => onOpen(point, event.currentTarget)}>
                                <span>
                                    {intl.formatMessage({ id: 'ServicesStatisticsPeaksChip' }, {
                                        period: chipTitle(intl, point, granularity),
                                        value: formatMinutes(value),
                                        count: point.attentions,
                                    })}
                                </span>
                                <i className="pi pi-angle-right" aria-hidden="true" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
